import type { Tinode } from '@inno-sdk/livechat-sdk'
import { innosQueryClient } from '@inno-tools/query'
import { toast } from '@inno-ui/toast'
import {
  GET_CM_RESPONSIBLE_QUERY_KEY,
  type GetCmResponsibleResponse,
  getCmResponsible
} from 'shared/api/afterSalesApi'
import {
  GET_LIVE_CHAT_CONTACT_PERSON_QUERY_KEY,
  getLiveChatContactPerson
} from 'shared/api/taskManagementApi'
import { i18nextContainer } from 'shared/configs/i18n/config'
import {
  CustomEvents,
  type LiveChatContact
} from 'shared/constants/customEvents'
import { logger } from 'shared/services/logger'
import {
  dispatchShortcutActionFailed as dispatchActionFailed,
  dispatchCustomEvent,
  listenCustomEvent
} from 'shared/utils/customEvents'
import { waitForLiveChatSession } from 'shared/utils/liveChat'

import { MY_BILLS_PATH } from '../constants/myBillsShortcut'
import { SUPPORT_CHAT_TIMEOUT_MS } from '../constants/shortcutLoading'
import type { SystemShortcutAction } from '../constants/systemShortcut.types'
import { getSystemShortcutDeepLink } from '../constants/systemShortcuts'
import { getApiErrorMessage } from './getApiErrorMessage'

type SupportChatStage = 'resolving' | 'opening_modal'

type CmResponsible = NonNullable<GetCmResponsibleResponse['data']>

interface SupportChat {
  chatGroupId: string
  contact: LiveChatContact
}

const getSupportContactPerson = (): Promise<CmResponsible | null> =>
  innosQueryClient
    .fetchQuery({
      queryKey: [GET_CM_RESPONSIBLE_QUERY_KEY],
      queryFn: getCmResponsible,
      staleTime: 0,
      cacheTime: 0
    })
    .then(responsible => responsible ?? null)

const toLiveChatContact = (responsible: CmResponsible): LiveChatContact => ({
  name:
    responsible.name?.trim() ||
    [responsible.first_name, responsible.last_name].filter(Boolean).join(' ') ||
    null,
  avatar: responsible.avatar || null
})

const getSupportChatGroupId = (
  contactPersonId: number
): Promise<string | null> =>
  innosQueryClient
    .fetchQuery({
      queryKey: [GET_LIVE_CHAT_CONTACT_PERSON_QUERY_KEY, contactPersonId],
      queryFn: () => getLiveChatContactPerson(contactPersonId),
      staleTime: 0,
      cacheTime: 0
    })
    .then(contactPerson => contactPerson?.chat_group_id ?? null)

export type CustomShortcutActionContext = {
  livechat?: typeof Tinode | null
  navigate?: (path: string) => void
  requestId?: string
}

const removeHandledShortcutParameters = (
  parameters: readonly string[]
): void => {
  const currentUrl = new URL(window.location.href)

  parameters.forEach(parameter => currentUrl.searchParams.delete(parameter))
  window.history.replaceState(
    window.history.state,
    '',
    `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`
  )
}

const resolveSupportChat = async (): Promise<SupportChat | null> => {
  const responsible = await getSupportContactPerson()
  if (!responsible?.id) return null

  const chatGroupId = await getSupportChatGroupId(responsible.id)
  return chatGroupId
    ? { chatGroupId, contact: toLiveChatContact(responsible) }
    : null
}

const notifySupportChatUnavailable = (error?: unknown): void => {
  toast.error({
    title: i18nextContainer.t('desktop.shortcut_item.support_unavailable'),
    subTitle:
      getApiErrorMessage(error) ??
      i18nextContainer.t('desktop.shortcut_item.support_unavailable_message')
  })
}

const notifySupportChatTimedOut = (): void => {
  toast.error({
    title: i18nextContainer.t('desktop.shortcut_item.support_timed_out'),
    subTitle: i18nextContainer.t(
      'desktop.shortcut_item.support_timed_out_message'
    )
  })
}

const dispatchShortcutActionFailed = (
  action: SystemShortcutAction,
  requestId?: string
): void => {
  if (!requestId) return

  dispatchActionFailed(action, requestId)
}

const isSessionAuthenticated = (livechat?: typeof Tinode | null): boolean =>
  livechat?.isAuthenticated() ?? false

const trackSupportChatAttempt = (
  livechat?: typeof Tinode | null,
  requestId?: string
) => {
  const startedAt = performance.now()
  let stage: SupportChatStage = 'resolving'
  let hasTimedOut = false
  let stopListening = () => {}
  let timer: ReturnType<typeof setTimeout> | undefined

  const elapsedMs = (): number => Math.round(performance.now() - startedAt)

  const settle = (): void => {
    clearTimeout(timer)
    stopListening()
  }

  if (requestId) {
    timer = setTimeout(() => {
      hasTimedOut = true
      settle()
      logger.warn('support_chat.timed_out', {
        stage,
        durationMs: elapsedMs(),
        isSessionAuthenticated: isSessionAuthenticated(livechat)
      })
      notifySupportChatTimedOut()
      dispatchActionFailed('openSupport', requestId)
    }, SUPPORT_CHAT_TIMEOUT_MS)

    const isThisAttempt = (event: { action: string; requestId: string }) =>
      event.action === 'openSupport' && event.requestId === requestId

    const stopListeningForReady = listenCustomEvent(
      CustomEvents.SHORTCUT_ACTION_READY,
      event => {
        if (!isThisAttempt(event)) return
        logger.track('support_chat.opened', { durationMs: elapsedMs() })
        settle()
      }
    )

    const stopListeningForFailure = listenCustomEvent(
      CustomEvents.SHORTCUT_ACTION_FAILED,
      event => {
        if (isThisAttempt(event)) settle()
      }
    )
    stopListening = () => {
      stopListeningForReady()
      stopListeningForFailure()
    }
  }

  return {
    elapsedMs,
    hasTimedOut: () => hasTimedOut,
    setStage: (next: SupportChatStage) => {
      stage = next
    }
  }
}

const openSupportChat = async (
  livechat?: typeof Tinode | null,
  requestId?: string
): Promise<void> => {
  const attempt = trackSupportChatAttempt(livechat, requestId)

  try {
    // The modal only mounts once the SDK has logged in, so overlap that
    // handshake with the lookups instead of paying for them in series.
    const [, supportChat] = await Promise.all([
      waitForLiveChatSession(livechat).then(() =>
        logger.track('support_chat.session_wait_completed', {
          durationMs: attempt.elapsedMs(),
          isSessionAuthenticated: isSessionAuthenticated(livechat)
        })
      ),
      resolveSupportChat().then(supportChat => {
        logger.track('support_chat.lookup_completed', {
          durationMs: attempt.elapsedMs(),
          hasChatGroup: Boolean(supportChat)
        })
        return supportChat
      })
    ])

    if (attempt.hasTimedOut()) return

    if (!supportChat) {
      logger.warn('support_chat.failed', {
        reason: 'no_chat_group',
        durationMs: attempt.elapsedMs()
      })
      notifySupportChatUnavailable()
      dispatchShortcutActionFailed('openSupport', requestId)
      return
    }

    attempt.setStage('opening_modal')
    // A `grp…` id is what makes this open as a group chat: the modal hands the
    // id straight to the SDK, which is topic-type agnostic.
    dispatchCustomEvent(CustomEvents.OPEN_LIVECHAT_USER_CHAT_MODAL, {
      topicId: supportChat.chatGroupId,
      requestId,
      contact: supportChat.contact
    })
  } catch (error) {
    if (attempt.hasTimedOut()) return

    logger.exception('support_chat.failed', error, {
      reason: 'lookup_error',
      durationMs: attempt.elapsedMs()
    })
    notifySupportChatUnavailable(error)
    dispatchShortcutActionFailed('openSupport', requestId)
  }
}

/** Executes an action selected by the registered system-shortcut definition. */
export const handleCustomShortcutAction = (
  action: SystemShortcutAction,
  context: CustomShortcutActionContext = {}
): void => {
  const { livechat, navigate } = context

  switch (action) {
    case 'openSupport':
      void openSupportChat(livechat, context.requestId)
      break
    case 'openNewGrantApplication':
      dispatchCustomEvent(CustomEvents.OPEN_NEW_GRANT_APPLICATION_MODAL)
      break
    case 'openMyBills':
      navigate?.(MY_BILLS_PATH)
  }
}

/** Handles custom actions encoded in the current application's deep link. */
export const handleCustomShortcutDeepLink = (
  value: string,
  context: CustomShortcutActionContext = {}
): boolean => {
  const definition = getSystemShortcutDeepLink(value)
  if (!definition?.action || !definition.deepLink) return false

  handleCustomShortcutAction(definition.action, context)
  removeHandledShortcutParameters(definition.deepLink.consumedSearchParams)
  return true
}
